/**
 * A component that allows users to search for a paint color by its code and name.
 *
 */
import {
	formHelperPropsSx,
	iconButtonSx,
	inputPropsSx,
	textFieldContainerSx,
	textFieldSx,
} from '@/components/blocks/ProductDetails/styles/searchColor';
import { saveColorCodeURL } from '@/components/blocks/ProductDetails/utils/utils';
import {
	BASE_COLOR_IDENTIFIER_SUFFIX,
	NO_RANGE_INDICATOR,
	NOT_APPLICABLE,
} from '@/components/content/ProductDetails/constants';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { usePaintColors } from '@/data/Content/usePaintColors';
import { useLocalization } from '@/data/Localization';
import { useColorDrawerState } from '@/data/state/useColorDrawerState';
import { LineRangeColor } from '@/data/types/ColorType';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import { ChangeEvent, FC, FormEvent, useState } from 'react';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';

const SEARCH_CODE_PAINT_REGEX = /^[a-zA-Z0-9- ]*$/;
interface SearchColorProps {
	onAttributeChange: (identifier: string, value: string) => void;
	brand: string;
	line: string | null | undefined;
}

const findColorInLines = (lines: LineRangeColor[], colorCode: string) =>
	lines
		.map((lineItem) => {
			const colorObj = lineItem.colors.find(
				(colorObj) =>
					colorObj.code.toLowerCase() === colorCode.toLowerCase().trim() ||
					colorObj.name.toLowerCase() === colorCode.toLowerCase().trim()
			);
			return colorObj ? colorObj : null;
		})
		.find((result) => result !== null);

export const SearchColor: FC<SearchColorProps> = ({ onAttributeChange, brand, line }) => {
	const { BuyByColor } = useLocalization('HDMProductDetail');
	const [colorCode, setColorCode] = useState('');
	const { lineRangeColors } = usePaintColors();
	const { colorDrawerState, actions: colorDrawerActions } = useColorDrawerState();
	const [searchError, setSearchError] = useState({ error: false, helperText: '' });
	const { error, helperText } = searchError;
	const [focusedSearchBar, setFocusedSearchBar] = useState(false);

	const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const val = event.target.value;
		if (val.trim() === '' || SEARCH_CODE_PAINT_REGEX.test(val)) {
			setColorCode(val);
		}
	};

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		setSearchError({ error: false, helperText: '' });

		if (!colorCode) {
			setSearchError({
				error: true,
				helperText: BuyByColor.Errors.required.t(),
			});
			return;
		}

		const matchingLines =
			lineRangeColors?.filter(
				(lineRange: LineRangeColor) => lineRange?.brand === brand && lineRange?.line === line
			) ?? [];

		// "#N/D" is a placeholder range (a base's own default swatch, e.g. Berel's
		// duplicated base color or Behr's "N/A" base entries). Prefer a match in a
		// real range, falling back to "#N/D" only when the color isn't filed anywhere else.
		const realRangeLines = matchingLines.filter(
			(lineRange) => lineRange.range !== NO_RANGE_INDICATOR
		);
		const colorCodeSelected =
			findColorInLines(realRangeLines, colorCode) ?? findColorInLines(matchingLines, colorCode);

		if (colorCodeSelected === undefined) {
			setSearchError({
				error: true,
				helperText: BuyByColor.Errors.notFound.t(),
			});
			return;
		}

		let changeBaseColor;
		if (
			colorDrawerState &&
			colorCodeSelected &&
			colorDrawerState?.base !== colorCodeSelected?.base
		) {
			const baseColorAttr = colorDrawerState?.superSKUAttributes?.find((attr) =>
				attr.identifier.includes(BASE_COLOR_IDENTIFIER_SUFFIX)
			);
			const baseValue = baseColorAttr?.values.find(
				(val) => val.identifier === colorCodeSelected?.base
			);

			if (baseColorAttr && baseValue) {
				const color = colorCodeSelected.code !== NOT_APPLICABLE ? colorCodeSelected.code : null;
				changeBaseColor = {
					name: baseColorAttr.identifier,
					value: baseValue.identifier,
					id: baseColorAttr.id,
					color,
				};
			}
		}
		if (colorCodeSelected) {
			saveColorCodeURL(colorCodeSelected?.code);
			colorDrawerActions.setColorDrawerData({
				isOpen: false,
				rangeColors: null,
				colorCodeSelected,
				range: colorCodeSelected?.range,
				colorCode: colorCodeSelected?.code,
				colorSelectedDrawer: colorCodeSelected,
				hasConfirmedColor: true,
			});
			if (changeBaseColor) {
				setColorCode(colorCodeSelected?.code);
				onAttributeChange(changeBaseColor?.name, changeBaseColor?.value ?? '');
			}
		}
	};

	return (
		<Box component="form" onSubmit={handleSubmit} noValidate>
			<Box sx={textFieldContainerSx}>
				<TextField
					margin="normal"
					name="colorCode"
					onChange={handleChange}
					value={colorCode}
					placeholder={BuyByColor.labels.buyByColorSearchColor.t()}
					inputProps={{ maxLength: 40 }}
					fullWidth
					autoComplete="off"
					error={error}
					helperText={helperText}
					FormHelperTextProps={{ sx: formHelperPropsSx }}
					sx={textFieldSx}
					onFocus={() => setFocusedSearchBar(true)}
					onMouseEnter={() => setFocusedSearchBar(true)}
					onMouseLeave={() => setFocusedSearchBar(false)}
					onBlur={() => setFocusedSearchBar(false)}
					InputProps={{
						sx: inputPropsSx({ hasError: Boolean(error) }),
						endAdornment: (
							<InputAdornment position="end">
								<IconButton disableRipple sx={iconButtonSx({ focusedSearchBar })} type="submit">
									<DynamicIcon name={MATERIAL_SYMBOL_ICONS.frameInspect} size={40} />
								</IconButton>
							</InputAdornment>
						),
					}}
				/>
			</Box>
		</Box>
	);
};
